/**
 * JobTaskList 页头三件套：
 * - JobStatTiles：仿真结果统计彩色瓷砖行（普通模式 + statsMode 共用）
 * - JobInfoGrid：任务元信息定义网格（label / value / full）
 * - SceneSetPopover：场景集合树 Popover（复用 JobList 表列 render 里那 100 行）
 *
 * 三个组件都是纯展示，数据获取由父组件负责，避免在 header 组件内 useEffect
 * 打乱页面数据流。
 */
import React from 'react';
import { Popover, Spin, Tooltip, Tree } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import type { FilteredTaskCountData, JobItem, JobTaskCountData, SceneTreeNode } from '@/apis/simulation';
import {
  countLeafNodes, convertSceneTreeToTreeForDisplay, findScenePath,
  type DisplayTreeNode,
} from '@/common/sceneSetTree';
import { decodeGroupBackendToken, sceneGroupDisplay } from '@/constants/sceneGroup';

// ===== JobStatTiles ========================================================

interface JobStatTilesProps {
  taskCount: JobTaskCountData | null;
  loading?: boolean;
  /** 普通模式：「不通过数」瓷砖变成可点击下钻 statsMode 页；statsMode 自身关掉。 */
  showFailDrillLink?: boolean;
  /** 已身处 statsMode 时给「不通过数」瓷砖使用者外链的路径；showFailDrillLink 才用到。 */
  failDrillHref?: string;
  /** 卡片右上角的 action 区（用于挂「按场景分类」按钮），完全由父组件决定。 */
  actions?: React.ReactNode;
}

// 配色对齐「任务对比」页 2×2 矩阵卡片：靛蓝 / 天蓝 / 绿 / 琥珀 / 红；次要瓷砖用中性灰
export const TILE_PASS_RATE = { color: '#5e6ad2', bg: '#eef0fb' };
export const TILE_TOTAL = { color: '#0284c7', bg: '#f0f9ff' };
export const TILE_PASS = { color: '#16a34a', bg: '#f0fdf4' };
export const TILE_FAIL = { color: '#d97706', bg: '#fffbeb' };
export const TILE_EXCEPTION = { color: '#dc2626', bg: '#fef2f2' };
export const TILE_MUTED = { color: '#4b5563', bg: '#f3f4f6' };

export interface TileSpec {
  key: string;
  label: string;
  value: React.ReactNode;
  color: string;
  bg: string;
  interactive?: boolean; // 悬停高亮 + 下划线，供下钻瓷砖使用
  onClick?: () => void;
  tooltip?: string;
}

export function JobStatTiles({
  taskCount,
  loading,
  showFailDrillLink,
  failDrillHref,
  actions,
}: JobStatTilesProps) {
  const passRateText =
    taskCount && taskCount.totalCount > 0
      ? `${((taskCount.passCount / taskCount.totalCount) * 100).toFixed(1)}%`
      : '-';

  const tiles: TileSpec[] = [
    { key: 'passRate', label: '通过率', value: passRateText, ...TILE_PASS_RATE },
    { key: 'total', label: '总数', value: taskCount?.totalCount ?? 0, ...TILE_TOTAL },
    { key: 'pass', label: '通过数', value: taskCount?.passCount ?? 0, ...TILE_PASS },
    {
      key: 'fail',
      label: '不通过数',
      value: taskCount?.failCount ?? 0,
      ...TILE_FAIL,
      interactive: !!(showFailDrillLink && failDrillHref),
      tooltip: showFailDrillLink
        ? '点击查看本 Job 不通过统计（按评测器明细 + 不通过任务列表）'
        : undefined,
      onClick: showFailDrillLink && failDrillHref
        ? () => window.open(failDrillHref, '_blank', 'noopener,noreferrer')
        : undefined,
    },
    { key: 'exception', label: '异常数', value: taskCount?.exceptionCount ?? 0, ...TILE_EXCEPTION },
    { key: 'ready', label: '待运行', value: taskCount?.readyCount ?? 0, ...TILE_MUTED },
    { key: 'running', label: '运行中', value: taskCount?.runningCount ?? 0, ...TILE_MUTED },
  ];

  const excludedNote =
    taskCount?.excludeCheckFailed && taskCount.excludedCheckFailedCount > 0 ? (
      <div style={{ color: '#9b9da3', fontSize: 12, marginBottom: 8 }}>
        已忽略 {taskCount.excludedCheckFailedCount} 个 check 不通过场景（不计入分子分母）
      </div>
    ) : null;

  return (
    <Spin spinning={!!loading && !taskCount}>
      {actions && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          {actions}
        </div>
      )}
      {excludedNote}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {tiles.map((t) => (
          <StatTile key={t.key} spec={t} />
        ))}
      </div>
    </Spin>
  );
}

export function StatTile({ spec }: { spec: TileSpec }) {
  const [hover, setHover] = React.useState(false);
  const clickable = !!spec.onClick;
  const tile = (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={spec.onClick}
      onKeyDown={
        clickable
          ? (e) => (e.key === 'Enter' || e.key === ' ') && spec.onClick?.()
          : undefined
      }
      onMouseEnter={clickable ? () => setHover(true) : undefined}
      onMouseLeave={clickable ? () => setHover(false) : undefined}
      style={{
        flex: '1 1 140px',
        minWidth: 120,
        padding: '14px 16px',
        background: spec.bg,
        border: `1px solid ${spec.color}33`,
        borderRadius: 8,
        textAlign: 'center',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'transform .12s ease, box-shadow .12s ease',
        transform: clickable && hover ? 'translateY(-1px)' : 'none',
        boxShadow: clickable && hover ? `0 2px 8px ${spec.color}22` : 'none',
      }}
    >
      <div
        style={{
          fontSize: 12,
          color: '#6b6f76',
          marginBottom: 6,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        {spec.label}
        {clickable && (
          <ArrowRightOutlined
            style={{
              fontSize: 10,
              color: spec.color,
              opacity: hover ? 1 : 0.5,
              transform: hover ? 'translateX(2px)' : 'none',
              transition: 'opacity .18s ease, transform .18s ease',
            }}
          />
        )}
      </div>
      <div
        style={{
          fontSize: 24,
          fontWeight: 700,
          color: spec.color,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.2,
        }}
      >
        {spec.value}
      </div>
    </div>
  );
  return spec.tooltip ? <Tooltip title={spec.tooltip}>{tile}</Tooltip> : tile;
}

// ===== FilteredStatRow =====================================================

interface FilteredStatRowProps {
  /** 「筛选后统计」数据；null 时整行不渲染（尚未查询 / 已重置）。 */
  count: FilteredTaskCountData | null;
  loading?: boolean;
}

/**
 * 查询区下方单独一行的「筛选后统计」：跟随当前筛选条件展示 场景数 / 通过 / 不通过 /
 * 异常（+ 任务总数）。紧凑内联样式，区别于顶部整-Job 彩色瓷砖（JobStatTiles）。
 * 口径与顶部瓷砖一致（Task.status 分桶）；场景数为 distinct 场景，故与 通过+不通过+异常
 * 分母不同，见 tooltip。
 */
export function FilteredStatRow({ count, loading }: FilteredStatRowProps) {
  if (!count && !loading) return null;
  const items: { label: string; value: number; color: string; tooltip?: string }[] = [
    {
      label: '场景数',
      value: count?.sceneCount ?? 0,
      color: TILE_TOTAL.color,
      tooltip: '当前筛选命中的去重场景数（一个场景可能有多个任务，故不等于 通过+不通过+异常）',
    },
    { label: '任务数', value: count?.totalCount ?? 0, color: TILE_MUTED.color },
    { label: '通过', value: count?.passCount ?? 0, color: TILE_PASS.color },
    { label: '不通过', value: count?.failCount ?? 0, color: TILE_FAIL.color },
    { label: '异常', value: count?.errorCount ?? 0, color: TILE_EXCEPTION.color },
  ];
  return (
    <Spin spinning={!!loading}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
          padding: '8px 12px',
          marginBottom: 12,
          background: '#fafafa',
          border: '1px solid #f0f0f0',
          borderRadius: 8,
          fontSize: 13,
        }}
      >
        <span style={{ color: '#9b9da3', marginRight: 4 }}>筛选结果</span>
        {items.map((it) => {
          const chip = (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'baseline',
                gap: 6,
                padding: '2px 10px',
                background: '#fff',
                border: `1px solid ${it.color}33`,
                borderRadius: 6,
              }}
            >
              <span style={{ color: '#6b6f76' }}>{it.label}</span>
              <span
                style={{
                  color: it.color,
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {it.value}
              </span>
            </span>
          );
          return (
            <React.Fragment key={it.label}>
              {it.tooltip ? <Tooltip title={it.tooltip}>{chip}</Tooltip> : chip}
            </React.Fragment>
          );
        })}
      </div>
    </Spin>
  );
}

// ===== JobInfoGrid =========================================================

export interface JobInfoField {
  label: string;
  value: React.ReactNode;
  /** true 时该字段独占整行（给场景集合 / 算法版本这类长值） */
  full?: boolean;
}

interface JobInfoGridProps {
  fields: JobInfoField[];
  loading?: boolean;
}

export function JobInfoGrid({ fields, loading }: JobInfoGridProps) {
  return (
    <Spin spinning={!!loading}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          rowGap: 12,
          columnGap: 32,
          fontSize: 13,
        }}
      >
        {fields.map((f) => (
          <div
            key={f.label}
            style={{
              display: 'flex',
              gap: 8,
              minWidth: 0,
              ...(f.full ? { gridColumn: '1 / -1' } : null),
            }}
          >
            {/* 84px：最长标签「Dagu Run ID」需要这么宽，72px 会把它折成两行。 */}
            <span style={{ color: '#9b9da3', flex: '0 0 84px' }}>{f.label}</span>
            <span
              style={{
                color: '#1f2937',
                fontWeight: 500,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {f.value}
            </span>
          </div>
        ))}
      </div>
    </Spin>
  );
}

// ===== SceneSetPopover =====================================================

interface SceneSetPopoverProps {
  sceneSetIds: string;
  sceneGroupRefs?: JobItem['sceneGroupRefs'];
  sceneSource?: string;
  sceneTree: SceneTreeNode[];
  placement?: 'right' | 'top' | 'bottom' | 'left';
  /** 触发方式（Antd Popover 兼容 string 或 string[]）；默认 click */
  trigger?: 'click' | 'hover';
}

/**
 * 场景集合 Popover：接受 JobItem 的 sceneSetIds + sceneGroupRefs + sceneSource,
 * 展示「N 个场景集」超链，点击弹出裁剪后的场景树。语义与老 JobList 表列 render 一致。
 */
export function SceneSetPopover({
  sceneSetIds,
  sceneGroupRefs,
  sceneSource,
  sceneTree,
  placement = 'right',
  trigger = 'click',
}: SceneSetPopoverProps) {
  // 按场景发起：sceneSetIds 存的是场景 id 串（非场景集树节点），不走树解析，直接展示数量
  if (sceneSource === 'scene') {
    const sceneCount = (sceneSetIds || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean).length;
    return <span>{sceneCount > 0 ? `${sceneCount}个场景` : '-'}</span>;
  }

  const ids: number[] = [];
  const groupsByLeaf = new Map<number, Set<string>>();
  const addGroup = (leafId: number, groupKey: string) => {
    if (!groupsByLeaf.has(leafId)) groupsByLeaf.set(leafId, new Set());
    groupsByLeaf.get(leafId)!.add(groupKey);
  };

  for (const raw of (sceneSetIds || '').split(',')) {
    const t = raw.trim();
    if (!t) continue;
    const asNum = parseInt(t, 10);
    if (!isNaN(asNum) && String(asNum) === t) {
      ids.push(asNum);
      continue;
    }
    const decoded = decodeGroupBackendToken(t);
    if (decoded) addGroup(decoded.leafId, decoded.groupKey);
  }
  for (const ref of sceneGroupRefs || []) {
    addGroup(ref.leafId, ref.groupKey);
  }
  for (const leafId of groupsByLeaf.keys()) {
    if (!ids.includes(leafId)) ids.push(leafId);
  }

  if (sceneTree.length === 0) {
    return <span>-</span>;
  }

  const foundIds = ids.filter((id) => findScenePath(sceneTree, id) !== null);
  if (foundIds.length === 0) {
    return <span>-</span>;
  }

  let treeData: DisplayTreeNode[] = convertSceneTreeToTreeForDisplay(sceneTree, foundIds);
  if (groupsByLeaf.size > 0) {
    const attachGroups = (nodes: DisplayTreeNode[]): DisplayTreeNode[] =>
      nodes.map((n) => {
        const groups = groupsByLeaf.get(Number(n.key));
        if (groups && groups.size > 0) {
          const virtualChildren: DisplayTreeNode[] = Array.from(groups).map((g) => ({
            title: sceneGroupDisplay(g),
            key: `${n.key}:${g}`,
            isLeaf: true,
          }));
          return { ...n, isLeaf: false, children: virtualChildren };
        }
        if (n.children && n.children.length > 0) {
          return { ...n, children: attachGroups(n.children) };
        }
        return n;
      });
    treeData = attachGroups(treeData);
  }

  const leafNodeCount = countLeafNodes(treeData);
  const displayText = leafNodeCount > 0 ? `${leafNodeCount}个场景集` : '-';

  return (
    <Popover
      title="场景集合"
      content={
        <div style={{ maxHeight: 400, overflow: 'auto', width: 300 }}>
          <Tree
            treeData={treeData as unknown as Parameters<typeof Tree>[0]['treeData']}
            defaultExpandAll
            selectedKeys={foundIds.map((id: number) => id.toString())}
            checkable={false}
            showLine={{ showLeafIcon: false }}
          />
        </div>
      }
      trigger={trigger}
      placement={placement}
    >
      <a style={{ color: '#5e6ad2', cursor: 'pointer' }}>{displayText}</a>
    </Popover>
  );
}
